"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.waitForTx = exports.hasSucceeded = exports.hasFailed = exports.isPending = exports.getTransactionOrigin = exports.getTransactionHref = exports.buildTransactionWithFromPayload = exports.buildTransactionWithReceiptPayload = exports.buildTransactionPayload = exports.getTransactionAddressFromAction = exports.getTransactionHashFromAction = exports.getTransactionFromAction = exports.isTransactionAction = exports.buildActionRef = exports.TRANSACTION_ACTION_FLAG = void 0;
const chain_id_1 = require("@dcl/schemas/dist/dapps/chain-id");
const effects_1 = require("redux-saga/effects");
const types_1 = require("./types");
const actions_1 = require("./actions");
// Special flag used to determine transaction hashes to be monitored
exports.TRANSACTION_ACTION_FLAG = '_watch_tx';
function buildActionRef(transaction) {
  const {
    actionType,
    withReceipt,
    payload
  } = transaction;
  const buildFunction = withReceipt ? buildTransactionWithReceiptPayload : buildTransactionPayload;
  return {
    type: actionType,
    payload: buildFunction(transaction.chainId, transaction.hash, payload)
  };
}
exports.buildActionRef = buildActionRef;
function isTransactionAction(action) {
  return !!getTransactionFromAction(action);
}
exports.isTransactionAction = isTransactionAction;
function getTransactionFromAction(action) {
  return action.payload && action.payload[exports.TRANSACTION_ACTION_FLAG];
}
exports.getTransactionFromAction = getTransactionFromAction;
function getTransactionHashFromAction(action) {
  return getTransactionFromAction(action).hash;
}
exports.getTransactionHashFromAction = getTransactionHashFromAction;
function getTransactionAddressFromAction(action) {
  return getTransactionFromAction(action).from;
}
exports.getTransactionAddressFromAction = getTransactionAddressFromAction;
function buildTransactionPayload(chainId, hash) {
  let payload = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return {
    [exports.TRANSACTION_ACTION_FLAG]: {
      chainId,
      hash,
      payload
    }
  };
}
exports.buildTransactionPayload = buildTransactionPayload;
function buildTransactionWithReceiptPayload(chainId, hash) {
  let payload = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const txPayload = buildTransactionPayload(chainId, hash, payload);
  txPayload[exports.TRANSACTION_ACTION_FLAG].withReceipt = true;
  return txPayload;
}
exports.buildTransactionWithReceiptPayload = buildTransactionWithReceiptPayload;
function buildTransactionWithFromPayload(chainId, hash, from) {
  let payload = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  const txPayload = buildTransactionPayload(chainId, hash, payload);
  txPayload[exports.TRANSACTION_ACTION_FLAG].from = from;
  return txPayload;
}
exports.buildTransactionWithFromPayload = buildTransactionWithFromPayload;
function getTransactionHref(_ref, network) {
  let {
    txHash,
    address,
    blockNumber
  } = _ref;
  const pathname = address ? "/address/".concat(address) : blockNumber ? "/block/".concat(blockNumber) : "/tx/".concat(txHash);
  return "".concat(getTransactionOrigin(network)).concat(pathname);
}
exports.getTransactionHref = getTransactionHref;
function getTransactionOrigin() {
  let chainId = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : chain_id_1.ChainId.ETHEREUM_MAINNET;
  switch (chainId) {
    case chain_id_1.ChainId.ETHEREUM_ROPSTEN:
      return 'https://ropsten.etherscan.io';
    case chain_id_1.ChainId.ETHEREUM_RINKEBY:
      return 'https://rinkeby.etherscan.io';
    case chain_id_1.ChainId.ETHEREUM_GOERLI:
      return 'https://goerli.etherscan.io';
    case chain_id_1.ChainId.ETHEREUM_SEPOLIA:
      return 'https://sepolia.etherscan.io';
    case chain_id_1.ChainId.MATIC_MAINNET:
      return 'https://explorer-mainnet.maticvigil.com';
    case chain_id_1.ChainId.MATIC_MUMBAI:
      return 'https://explorer-mumbai.maticvigil.com';
    default:
      return 'https://etherscan.io';
  }
}
exports.getTransactionOrigin = getTransactionOrigin;
function isPending(status) {
  return !types_1.FINISHED_STATUS.includes(status);
}
exports.isPending = isPending;
function hasFailed(status) {
  return types_1.FAILED_STATUS.includes(status);
}
exports.hasFailed = hasFailed;
function hasSucceeded(status) {
  return types_1.SUCCESS_STATUS.includes(status);
}
exports.hasSucceeded = hasSucceeded;
/**
 * Waits for a transaction to be completed.
 *
 * @param txHash - The hash of the transaction to wait for.
 */
function* waitForTx(txHash) {
  let txHashToWaitFor = txHash;
  while (true) {
    const {
      success,
      failure
    } = yield (0, effects_1.race)({
      success: (0, effects_1.take)(actions_1.FETCH_TRANSACTION_SUCCESS),
      failure: (0, effects_1.take)(actions_1.FETCH_TRANSACTION_FAILURE)
    });
    if ((success === null || success === void 0 ? void 0 : success.payload.transaction.hash) === txHashToWaitFor) {
      break;
    } else if (failure && failure.payload.transaction.hash === txHashToWaitFor) {
      if (failure.payload.status === types_1.TransactionStatus.DROPPED) {
        let continueWaiting = true;
        // If the transaction was dropped, follow the procedure to check what to do
        while (true) {
          const {
            replace,
            fetchAgain,
            update
          } = yield (0, effects_1.race)({
            replace: (0, effects_1.take)(actions_1.REPLACE_TRANSACTION_SUCCESS),
            fetchAgain: (0, effects_1.take)(actions_1.FETCH_TRANSACTION_REQUEST),
            update: (0, effects_1.take)(actions_1.UPDATE_TRANSACTION_STATUS)
          });
          if (fetchAgain && fetchAgain.payload.hash === txHashToWaitFor) {
            // Re start the transaction fetching process.
            txHashToWaitFor = fetchAgain.payload.hash;
            continueWaiting = true;
            break;
          } else if (replace && replace.payload.hash === txHashToWaitFor) {
            // The transaction hash was replaced for another one, track the other transaction hash.
            txHashToWaitFor = replace.payload.replaceBy;
            continueWaiting = true;
            break;
          } else if (update && update.payload.hash === txHashToWaitFor && update.payload.status === types_1.TransactionStatus.REPLACED) {
            // A new hash wasn't found, but the transaction was replaced. We should consider the wait finished because we don't know if it failed.
            continueWaiting = false;
            break;
          }
        }
        if (continueWaiting) {
          continue;
        }
        break;
      } else {
        throw new Error("The transaction ".concat(txHash, " failed to be mined. The status is ").concat(failure.payload.status, "."));
      }
    }
  }
}
exports.waitForTx = waitForTx;